import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlbums } from '../contexts/AlbumContext';
import { motion } from 'framer-motion';

const CreateAlbum = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    isPublic: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { user } = useAuth();
  const { createAlbum } = useAlbums();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const albumData = {
        ...formData,
        ownerId: user.id,
        ownerName: user.name
      };

      const result = await createAlbum(albumData);
      
      if (result.success) {
        navigate(`/album/${result.album.id}`);
      } else {
        setError(result.error || 'Failed to create album');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-2xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-12 text-center">
            <h1 className="text-4xl font-light text-black mb-3 tracking-tight">Create New Album</h1>
            <p className="text-gray-600 font-light">Set up a photo album for your event</p>
          </div>

          <div className="border border-gray-200 rounded-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-black mb-3">
                  Album Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-light"
                  placeholder="e.g., Sarah's Wedding Reception"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-black mb-3">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-light resize-none"
                  placeholder="Tell people about your event and encourage them to share photos..."
                />
              </div>

              <div>
                <label htmlFor="eventDate" className="block text-sm font-medium text-black mb-3">
                  Event Date
                </label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-light"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPublic"
                  name="isPublic"
                  checked={formData.isPublic}
                  onChange={handleChange}
                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                />
                <label htmlFor="isPublic" className="ml-3 text-sm text-gray-700 font-light">
                  Allow anyone with the link to upload photos
                </label>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-sm font-medium text-black mb-3">What happens next?</h3>
                <ul className="text-sm text-gray-600 space-y-2 font-light">
                  <li>• You'll get a shareable link and QR code</li>
                  <li>• Anyone can upload photos using the link</li>
                  <li>• Photos appear in a beautiful grid layout</li>
                  <li>• You can download all photos anytime</li>
                </ul>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium transition-all btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all btn-primary"
                >
                  {loading ? 'Creating...' : 'Create Album'}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateAlbum;